<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            @click="() => $router.push({ name: 'gestoesDeAulas' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Vínculo
        </h1>
      </v-col>
    </v-row>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        @submit.prevent="handleSubmit(submitForm)"
        :disabled="submittingForm"
        class="pa-4 gestao-form"
      >
        <v-row class="pl-2 rounded-lg gestao-form__header">
          <v-col cols="12" md="6" lg="8">
            <ValidationProvider name="Professor" rules="required" v-slot="{ errors }">
              <e-label>Professor</e-label>
              <e-autocomplete
                :items="professoresList"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(val) => `${val.cpf} - ${val.nome} - ${val.user.roles[0].name}`"
                :error-messages="errors"
                :loading="carregandoProfessores"
                v-model="form.instrutor_id"
                label="Selecione uma opção"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6" lg="4">
            <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
              <e-label>Tipo do Professor</e-label>
              <v-select
                :items="tipos"
                :item-value="(value) => value.id"
                :item-text="(val) => val.descricao"
                label="Selecione uma opção"
                v-model="form.tipo_professor"
                :error-messages="form.errors ? form.errors : errors"
                dense
                solo
              ></v-select>
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row
          v-for="relacao of relacoes"
          :key="relacoes.indexOf(relacao)"
          class="animate__animated animate__zoomIn mb-6 pa-2 rounded-lg elevation-1 gestao-form__relacao"
          style="animation-duration: 0.5s"
        >
          <v-col cols="12" md="6" lg="3">
            <ValidationProvider name="Turma" rules="required" v-slot="{ errors }">
              <e-label>Turma</e-label>
              <e-autocomplete
                :items="turmasList"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(val) => val.descricao"
                :error-messages="relacao.errors ? relacao.errors : errors"
                :loading="carregandoTurmas"
                v-model="relacao.turma_id"
                :disabled="editing"
                @change="onTurmaChange(relacao), getEtapasDaTurma(relacao.turma_id)"
                label="Selecione uma opção"
                dense
                solo
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="2">
            <v-container class="pa-0" fluid>
              <v-switch
                v-model="relacao.is_polivalencia"
                :label="`Polivalência`"
                :disabled="editing"
                @change="onPolivalenciaChange(relacao)"
              ></v-switch>
            </v-container>
          </v-col>

          <v-col cols="12" md="6" lg="3">
            <ValidationProvider
              v-show="!relacao.is_polivalencia"
              name="Componente Curricular"
              rules="required"
              v-slot="{ errors }"
            >
              <e-label>Componente Curricular</e-label>
              <e-autocomplete
                :items="relacao.disciplinasDisponiveis"
                :return-object="false"
                :item-value="(value) => value.id"
                :item-text="(val) => val.descricao"
                :error-messages="relacao.errors ? relacao.errors : errors"
                :loading="relacao.carregandoDisciplinas"
                @change="calcularCargaHoraria(relacao)"
                v-model="relacao.disciplina_id"
                :disabled="editing"
                label="Selecione uma opção"
                dense
                solo
              />
            </ValidationProvider>

            <ValidationProvider v-show="relacao.is_polivalencia" name="Componente Curricular">
              <e-label>Componente(s) Curricular(es)</e-label>
              <v-autocomplete
                v-show="!editing"
                v-model="relacao.disciplinas_polivalencia"
                :item-value="(value) => value.id"
                :item-text="(val) => val.descricao"
                :items="relacao.disciplinasDisponiveis"
                :loading="relacao.carregandoDisciplinas"
                @change="calcularCargaHoraria(relacao)"
                chips
                label="Selecione os Componentes Curriculares"
                multiple
                solo
              ></v-autocomplete>

              <div v-show="editing" v-for="(item, index) in relacao.disciplinas" :key="index">
                <v-chip x-small>{{ item.descricao }}</v-chip>
              </div>
            </ValidationProvider>
          </v-col>

          <v-col class="pt-4 pb-0" cols="12" sm="6" md="6" lg="3">
            <v-container class="pa-0" fluid>
              <v-checkbox
                class="custom-checkbox"
                v-model="relacao.nao_compor_boletim_nem_ata"
                :label="`Não compõe Boletim e Ata Final`"
                :disabled="!!relacao.is_polivalencia"
              ></v-checkbox>
            </v-container>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg="1">
            <ValidationProvider name="Carga horária (horas)" rules="required" v-slot="{ errors }">
              <e-label>Carga horária <small>(horas)</small></e-label>
              <v-text-field
                type="number"
                v-model="relacao.carga_horaria"
                dense
                solo
                readonly
                :error-messages="errors"
                min="0"
              />
            </ValidationProvider>
          </v-col>

          <v-col v-if="!editing" cols="12" xl="5">
            <v-card class="gestao-form__info-card">
              <v-card-title class="gestao-form__card-title">
                <e-label>Relação de Dias e Horários</e-label>
              </v-card-title>
              <v-card-text>
                <div v-if="relacao.horarios && relacao.horarios.length > 0">
                  <v-row>
                    <v-col cols="7" class="text-left">
                      <span class="text-uppercase">Horário</span>
                    </v-col>
                    <v-col cols="5" class="text-left">
                      <span class="text-uppercase">Dia</span>
                    </v-col>
                  </v-row>
                  <v-list>
                    <v-list-item v-for="(item, i) in relacao.horarios" :key="i">
                      <v-row>
                        <v-col cols="7" class="text-left">
                          {{ item.horario.descricao }}
                        </v-col>
                        <v-col cols="5" class="text-left">
                          {{ item.dia.descricao }}
                        </v-col>
                      </v-row>
                    </v-list-item>
                  </v-list>
                </div>
                <div v-else>
                  <p class="text-center">Não há horários selecionados</p>
                </div>
                <v-btn
                  color="green white--text"
                  class="mt-3"
                  :block="isMobile"
                  @click="alterRelacaoIdDialog(relacao)"
                >
                  Dias e horários
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="editing" cols="12" xl="5">
            <v-card class="gestao-form__info-card">
              <v-card-title class="gestao-form__card-title">
                <e-label>Relação de Dias e Horários</e-label>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="7" class="text-left">
                    <span class="text-uppercase">Horário</span>
                  </v-col>
                  <v-col cols="5" class="text-left">
                    <span class="text-uppercase">Dia</span>
                  </v-col>
                </v-row>
                <v-list>
                  <v-list-item v-for="(item, i) in relacoesDiasHorariosEdit" :key="i">
                    <v-row>
                      <v-col cols="7" class="text-left">
                        {{ item.horario.descricao }}
                      </v-col>
                      <v-col cols="5" class="text-left">
                        {{ item.dia.descricao }}
                      </v-col>
                    </v-row>
                  </v-list-item>
                </v-list>
                <v-btn
                  color="green white--text"
                  class="mt-3"
                  :block="isMobile"
                  @click="alterRelacaoIdDialog(relacao)"
                >
                  Dias e horários
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="!isTurmaInfantil(relacao.turma_id)" cols="12" xl="5">
            <v-card class="gestao-form__info-card">
              <v-card-title class="gestao-form__card-title">
                <e-label>Quantidade de Aulas por etapa</e-label>
              </v-card-title>

              <v-card-text v-if="relacao.turma_id && turmaHasCircuito(relacao.turma_id)">
                <v-row>
                  <v-col
                    v-for="etapa in etapasBimestraisList"
                    :key="etapa.id"
                    cols="12"
                    sm="6"
                    class="pa-4"
                  >
                    <ValidationProvider
                      :name="`Aulas da etapa ${etapa.descricao}`"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        type="number"
                        min="0"
                        :label="`${etapa.descricao}`"
                        v-model="relacao.relacaoAulasEtapas[etapa.id]"
                        dense
                        :error-messages="errors"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-alert
                v-else-if="relacao.turma_id && !turmaHasCircuito(relacao.turma_id)"
                dense
                type="warning"
                class="pa-4"
              >
                <span>
                  Esta turma não está vinculada a um circuito de nota. Primeiro vincule a turma a um
                  circuito seguindo: Turmas → editar turma → selecionar um circuito de nota.
                </span>
              </v-alert>

              <div v-else class="pa-4">
                <p class="text-center">
                  <v-icon lg class="mr-2" color="primary">mdi mdi-alert</v-icon>
                  <span>Selecione uma turma primeiro!</span>
                </p>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="1" class="d-flex align-start justify-end gestao-form__remove-col">
            <v-btn
              v-if="relacoes.length > 1"
              color="error"
              class="gestao-form__remove-btn"
              @click="() => trashRelacao(relacoes, relacao)"
              :disabled="submittingForm"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" :class="isMobile ? '' : 'd-flex justify-space-between'">
            <div :class="isMobile ? 'd-flex flex-column' : 'd-flex'">
              <v-btn
                color="primary"
                :block="isMobile"
                :disabled="submittingForm || anyTurmaWithoutCircuito"
                :loading="submittingForm"
                type="submit"
              >
                Enviar Dados
              </v-btn>
              <v-btn
                v-if="!editing"
                :class="isMobile ? 'mt-3' : 'ml-3'"
                color="secondary"
                :block="isMobile"
                @click="() => handleSubmit(adicionarRelacao)"
                :disabled="submittingForm || anyTurmaWithoutCircuito"
              >
                Adicionar
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-dialog transition="dialog-top-transition" v-model="dialogVisible" max-width="800">
          <template>
            <v-card>
              <v-toolbar color="primary" dark>
                Vinculo de dias e horários
                <v-spacer></v-spacer>
                <v-btn icon @click="dialogVisible = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>

              <v-card-text class="pb-0">
                <v-row class="mt-4">
                  <v-col class="pt-0 pb-0" cols="12" md="4">
                    <e-label>Dias da semana</e-label>
                    <v-select
                      v-model="dia_da_semana"
                      :items="diasDaSemana"
                      :return-object="true"
                      :item-value="(value) => value"
                      :item-text="(value) => value.descricao"
                      dense
                      solo
                      label="selecione"
                    ></v-select>
                  </v-col>

                  <v-col class="pt-0 pb-0" cols="12" md="4">
                    <e-label>Horarios</e-label>
                    <v-select
                      v-model="horario_selecionado_id"
                      :items="horariosList"
                      :return-object="false"
                      :item-value="(value) => value"
                      :item-text="(value) => value.descricao"
                      dense
                      solo
                      label="selecione"
                    ></v-select>
                  </v-col>

                  <v-col class="pt-0 pb-0 d-flex align-end" cols="12" md="4">
                    <v-btn
                      :disabled="!dia_da_semana || !horario_selecionado_id || carregandoHorarios"
                      :loading="carregandoHorarios"
                      color="green"
                      :block="isMobile"
                      class="white--text"
                      @click="adicionarHorario(relacoes.indexOf(dialogVisibleRelacaoId))"
                    >
                      + adicionar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-col v-if="!editing" class="pt-2">
                <v-timeline
                  v-for="(item, i) in relacoesDiasHorarios"
                  :key="i"
                  v-show="relacoes.indexOf(dialogVisibleRelacaoId) == item.relacao_id"
                  align-top
                  dense
                >
                  <v-timeline-item>
                    <v-chip
                      class="mt-1 gestao-form__timeline-chip"
                      color="green"
                      text-color="white"
                    >
                      {{ item.dia.descricao }} | {{ item.horario.descricao }}
                    </v-chip>
                    <v-btn
                      class="ml-2"
                      @click="deleteRelacaoDiaHorario(i)"
                      color="error"
                      fab
                      small
                      dark
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-timeline-item>
                </v-timeline>
              </v-col>

              <v-col v-if="editing" class="pt-2">
                <v-timeline v-for="(item, i) in relacoesDiasHorariosEdit" :key="i" align-top dense>
                  <v-timeline-item>
                    <v-chip
                      class="mt-1 gestao-form__timeline-chip"
                      color="green"
                      text-color="white"
                    >
                      {{ item.horario.descricao }} | {{ item.dia.descricao }}
                    </v-chip>
                    <v-btn
                      class="ml-2"
                      @click="deleteRelacaoDiaHorario(i)"
                      color="error"
                      fab
                      small
                      dark
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-timeline-item>
                </v-timeline>
              </v-col>
            </v-card>
          </template>
        </v-dialog>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

export default {
  components: { ELabel },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    anyTurmaWithoutCircuito() {
      try {
        return this.relacoes.some(
          (r) =>
            r.turma_id && !this.isTurmaInfantil(r.turma_id) && !this.turmaHasCircuito(r.turma_id)
        );
      } catch (e) {
        return false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
  data() {
    return {
      msgDiasEHorariosObrigatorios: false,
      selectMultiplo: false,
      tipos: [],
      horariosList: [],
      submittingForm: false,
      professoresList: [],
      turmasList: [],
      form: {},
      relacoes: [
        {
          instrutor_id: null,
          tipo_professor: null,
          turma_id: null,
          disciplina_id: null,
          disciplinas_polivalencia: [],
          disciplinasDisponiveis: [],
          horarios: [],
          carga_horaria: 0,
          is_polivalencia: false,
          nao_compor_boletim_nem_ata: false,
          relacoesDiasHorarios: [],
          relacaoAulasEtapas: {},
        },
      ],
      disciplinasList: [],
      carregandoProfessores: false,
      carregandoTurmas: false,
      carregandoDisciplinas: false,
      camposObrigatoriosNaoPreenchidos: false,
      relacoesDiasHorarios: [],
      relacoesDiasHorariosEdit: [],
      horario_selecionado_id: null,
      dia_da_semana: null,
      dialogVisible: false,
      dialogVisibleRelacaoId: 0,
      diasDaSemana: [],
      carregandoHorarios: false,
      etapasBimestraisList: [],
    };
  },
  methods: {
    trashRelacao(relacoes, relacao) {
      this.relacoes.splice(this.relacoes.indexOf(relacao), 1);
      this.$toast.success("Relação deletada com sucesso!");
    },
    alterRelacaoIdDialog(relacao) {
      this.dia_da_semana = null;
      this.horario_selecionado_id = null;
      this.dialogVisible = !this.dialogVisible;
      this.dialogVisibleRelacaoId = relacao;
      this.getHorariosDaTurma(relacao.turma_id);

      try {
        const turma = this.turmasList.find((t) => t.id === relacao.turma_id);
        if (turma && turma.dias_da_semana && Array.isArray(turma.dias_da_semana)) {
          const diasMap = {
            "segunda-feira": 0,
            "terça-feira": 1,
            "quarta-feira": 2,
            "quinta-feira": 3,
            "sexta-feira": 4,
            sábado: 5,
            domingo: 6,
          };
          this.diasDaSemana = turma.dias_da_semana
            .map((d) => ({
              id: diasMap[d.toLowerCase()] !== undefined ? diasMap[d.toLowerCase()] : d,
              descricao: d,
            }))
            .sort((a, b) => a.id - b.id);
        } else {
          this.diasDaSemana = [];
        }
      } catch (e) {
        this.diasDaSemana = [];
      }
    },
    async adicionarRelacao() {
      this.dia_da_semana = null;
      this.horario_selecionado_id = null;
      const novaRelacao = {
        instrutor_id: null,
        tipo_professor: null,
        turma_id: null,
        disciplina_id: null,
        disciplinas_polivalencia: [],
        disciplinasDisponiveis: [],
        horarios: [],
        carga_horaria: 0,
        is_polivalencia: false,
        nao_compor_boletim_nem_ata: false,
        relacoesDiasHorarios: [],
        relacaoAulasEtapas: {},
      };
      this.relacoes.push(novaRelacao);
    },
    async adicionarHorario(relacao) {
      if (!this.dia_da_semana || !this.horario_selecionado_id) {
        this.camposObrigatoriosNaoPreenchidos = true;
        return;
      }

      const horario = this.horariosList.find((item) => item.id === this.horario_selecionado_id);
      const data = {
        relacao_id: relacao,
        horario: horario || this.horario_selecionado_id,
        dia: this.dia_da_semana,
      };

      if (!this.editing) {
        this.relacoesDiasHorarios.push(data);
        if (!this.relacoes[relacao].horarios) {
          this.$set(this.relacoes[relacao], "horarios", [data]);
        } else {
          this.relacoes[relacao].horarios.push(data);
        }
      }

      if (this.editing) {
        this.relacoesDiasHorariosEdit.push(data);
      }

      this.camposObrigatoriosNaoPreenchidos = false;
    },

    deleteRelacaoDiaHorario(itemIndex) {
      const sourceList = this.editing ? this.relacoesDiasHorariosEdit : this.relacoesDiasHorarios;
      const item = sourceList[itemIndex];

      if (!item) return;

      this.$modals.danger({
        title: "Cuidado!",
        message: "Tem certeza que deseja apagar esse vínculo?",
        buttons: [
          {
            text: "Cancelar",
            callback: () => {
              this.$modals.close();
            },
            props: {
              color: "warning",
              small: true,
            },
          },
          {
            text: "Confirmar",
            disableIfCodeDoesntMatch: false,
            callback: async () => {
              this.$modals.close();
              this.$loaderService.open("Deletando o vínculo");
              try {
                this.$toast.success("Vínculo deletado com sucesso");

                sourceList.splice(itemIndex, 1);

                this.relacoes.forEach((relacao) => {
                  if (relacao && relacao.horarios) {
                    const horarioIndex = relacao.horarios.findIndex(
                      (horario) =>
                        horario.relacao_id === item.relacao_id &&
                        horario.dia?.id === item.dia?.id &&
                        horario.horario?.id === item.horario?.id
                    );
                    if (horarioIndex !== -1) {
                      relacao.horarios.splice(horarioIndex, 1);
                    }
                  }
                });

                this.$forceUpdate();
              } catch (error) {
                this.$handleError(error);
              }
              this.$loaderService.close();
            },
            props: {
              color: "error",
              small: true,
            },
          },
        ],
      });
    },

    async submitForm() {
      this.submittingForm = true;

      try {
        if (this.editing) {
          const formVinculo = {
            ...this.relacoes[0],
            instrutor_id: this.form.instrutor_id,
            tipo_professor: this.form.tipo_professor,
            relacoesDiasHorarios: this.relacoesDiasHorariosEdit,
          };

          if (formVinculo.relacoesDiasHorarios.length === 0) {
            this.$toast.danger("Preencha os campos obrigatórios: Dias da semana e Horários.");
            this.submittingForm = false;
            return;
          }

          await this.$services.gestoesDeAulasService.atualizar(formVinculo);
          this.$toast.success("Vínculo atualizado com sucesso!");
        }

        if (!this.editing) {
          const formRelacoes = this.relacoes.map((relacao, index) => ({
            ...relacao,
            instrutor_id: this.form.instrutor_id,
            tipo_professor: this.form.tipo_professor,
            is_polivalencia: relacao.is_polivalencia ? relacao.is_polivalencia : 0,
            relacoesDiasHorarios: this.relacoesDiasHorarios.filter((r) => r.relacao_id === index),
          }));

          if (formRelacoes.some((relacao) => relacao.relacoesDiasHorarios.length === 0)) {
            this.$toast.danger("Preencha os campos obrigatórios: Dias da semana e Horários.");
            this.submittingForm = false;
            return;
          }

          const responseData = await this.$services.gestoesDeAulasService.criar(formRelacoes);

          this.relacoes = responseData.filter((item) => item.errors);
          this.$toast.success("Vínculo(s) criado(s) com sucesso!");
        }

        if (this.relacoes.length === 0 || this.editing) {
          this.$toast.success("Vínculos criados/atualizados com sucesso");
          this.$router.push({ name: "gestoesDeAulas" });
        }
      } catch (error) {
        this.$handleError(error);
      }
      this.submittingForm = false;
    },

    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");
        this.professoresList = await this.$services.professoresService.syncAll();
        this.tipos = await this.$services.professoresService.tipos();
        this.turmasList = await this.$services.turmasService.syncAll();
        this.disciplinasList = await this.$services.disciplinasService.syncAll();
        this.horariosList = await this.$services.horariosService.syncAll();

        if (this.editing) {
          const vinculo = await this.$services.gestoesDeAulasService.gestaoGeralId(
            parseInt(this.$route.params.gestaoDeAulasId, 10)
          );

          this.relacoesDiasHorariosEdit = vinculo.relacoesDiasHorarios ?? [];
          this.form.is_polivalencia = vinculo.is_polivalencia;

          if (vinculo.tipo_professor) {
            this.form.tipo_professor = vinculo.tipo_professor;
          }
          if (vinculo) {
            if (vinculo.is_polivalencia && vinculo.disciplinas) {
              vinculo.disciplinas_polivalencia = vinculo.disciplinas.map((d) => d.id);
            }

            this.form.instrutor_id = vinculo.instrutor_id;
            vinculo.relacaoAulasEtapas = vinculo.relacaoAulasEtapas || {};
            this.relacoes = [
              {
                ...vinculo,
                disciplinasDisponiveis: vinculo.disciplinasDisponiveis || [],
                horarios: vinculo.horarios || [],
              },
            ];

            if (this.relacoes[0] && this.relacoes[0].turma_id) {
              await this.getDisciplinasDaTurma(this.relacoes[0]);
              this.calcularCargaHoraria(this.relacoes[0]);
            }
          }
          await this.getEtapasDaTurma(vinculo.turma_id);
          this.getHorariosDaTurma(vinculo.turma_id);
        }
        this.$loaderService.close();
        this.$forceUpdate();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async onTurmaChange(relacao) {
      relacao.disciplina_id = null;
      relacao.disciplinas_polivalencia = [];
      this.getHorariosDaTurma(relacao.turma_id);
      this.getDisciplinasDaTurma(relacao);
    },
    isTurmaInfantil(turma_id) {
      if (!turma_id) return false;
      const turma = this.turmasList.find((t) => t.id === turma_id);
      return turma && turma.isInfantil === true;
    },
    onPolivalenciaChange(relacao) {
      if (relacao.is_polivalencia) {
        this.$set(relacao, "nao_compor_boletim_nem_ata", false);
        this.$set(relacao, "disciplina_id", null);
      }
      if (!relacao.is_polivalencia) {
        this.$set(relacao, "disciplina_id", null);
        this.$set(relacao, "disciplinas_polivalencia", []);
      }
      this.calcularCargaHoraria(relacao);
    },
    async getDisciplinasDaTurma(relacao) {
      if (!relacao.turma_id) {
        this.$set(relacao, "disciplinasDisponiveis", []);
        return;
      }
      this.$set(relacao, "carregandoDisciplinas", true);
      try {
        const disciplinas = await this.$services.turmasService.getDisciplinas(relacao.turma_id);
        this.$set(relacao, "disciplinasDisponiveis", disciplinas);
      } catch (error) {
        this.$toast.danger("Erro ao carregar os componentes curriculares da turma.");
        this.$set(relacao, "disciplinasDisponiveis", []);
      } finally {
        this.$set(relacao, "carregandoDisciplinas", false);
      }
    },
    calcularCargaHoraria(relacao) {
      let cargaTotal = 0;
      const disciplinasDisponiveis = relacao.disciplinasDisponiveis || [];
      if (relacao.is_polivalencia) {
        if (relacao.disciplinas_polivalencia && relacao.disciplinas_polivalencia.length > 0) {
          relacao.disciplinas_polivalencia.forEach((disciplinaId) => {
            const disciplina = disciplinasDisponiveis.find((d) => d.id === disciplinaId);
            if (disciplina && disciplina.pivot && disciplina.pivot.carga_horaria_disciplina) {
              cargaTotal += parseFloat(disciplina.pivot.carga_horaria_disciplina);
            }
          });
        }
      } else if (relacao.disciplina_id) {
        const disciplina = disciplinasDisponiveis.find((d) => d.id === relacao.disciplina_id);
        if (disciplina && disciplina.pivot && disciplina.pivot.carga_horaria_disciplina) {
          cargaTotal = parseFloat(disciplina.pivot.carga_horaria_disciplina);
        }
      }
      this.$set(relacao, "carga_horaria", cargaTotal);
    },
    async getHorariosDaTurma(turmaParam) {
      this.$loaderService.open("Carregando horários");
      this.carregandoHorarios = true;
      try {
        const turma_id =
          turmaParam && typeof turmaParam === "object" ? turmaParam.turma_id : turmaParam;

        if (turma_id) {
          try {
            const turma = this.turmasList.find((t) => t.id === turma_id);
            if (turma && turma.dias_da_semana && Array.isArray(turma.dias_da_semana)) {
              const diasMap = {
                "segunda-feira": 0,
                "terça-feira": 1,
                "quarta-feira": 2,
                "quinta-feira": 3,
                "sexta-feira": 4,
                sábado: 5,
                domingo: 6,
              };
              this.diasDaSemana = turma.dias_da_semana.map((d) => ({
                id: diasMap[d.toLowerCase()] !== undefined ? diasMap[d.toLowerCase()] : d,
                descricao: d,
              }));
            } else {
              this.diasDaSemana = [];
            }
          } catch (e) {
            this.diasDaSemana = [];
          }

          this.horariosList = await this.$services.horariosService.getHorariosDoTurnoDaTurma(
            turma_id
          );
        } else {
          this.horariosList = [];
          this.diasDaSemana = [];
        }
      } catch (error) {
        this.$toast.error("Erro ao carregar horários", error);
      } finally {
        this.$loaderService.close();
        this.carregandoHorarios = false;
      }
    },

    async getEtapasDaTurma(turma_id) {
      this.$loaderService.open("Carregando etapas");
      try {
        if (!this.turmaHasCircuito(turma_id)) {
          this.etapasBimestraisList = [];
          if (this.editing && this.relacoes[0]) {
            this.relacoes[0].relacaoAulasEtapas = {};
          }
          return;
        }

        const response = await this.$services.etapasService.getEtapasBimestrais(turma_id);
        this.etapasBimestraisList = response;

        if (this.editing && this.relacoes[0]) {
          response.forEach((etapa) => {
            if (
              !Object.prototype.hasOwnProperty.call(this.relacoes[0].relacaoAulasEtapas, etapa.id)
            ) {
              this.$set(this.relacoes[0].relacaoAulasEtapas, etapa.id, "");
            }
          });
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },
    turmaHasCircuito(turmaParam) {
      try {
        const turma_id =
          turmaParam && typeof turmaParam === "object" ? turmaParam.turma_id : turmaParam;
        const turma = this.turmasList.find((t) => t.id === turma_id);
        return Boolean(turma && turma.circuito_nota_id);
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

<style>
.gestao-form {
  width: 100%;
}

.gestao-form__header {
  margin-bottom: 8px;
}

.gestao-form__relacao {
  background: #fff;
}

.gestao-form__info-card {
  height: 100%;
  border-radius: 14px;
}

.gestao-form__card-title {
  word-break: break-word;
}

.gestao-form__remove-col {
  min-height: 56px;
}

.gestao-form__remove-btn {
  min-width: 44px;
}

.gestao-form__timeline-chip {
  max-width: 100%;
  white-space: normal;
  height: auto !important;
  line-height: 1.35;
  padding-top: 6px;
  padding-bottom: 6px;
}

@media (max-width: 960px) {
  .gestao-form {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .gestao-form__header {
    margin-bottom: 12px;
  }

  .gestao-form__relacao {
    padding: 12px !important;
    margin-bottom: 16px !important;
  }

  .gestao-form__info-card .v-card__title {
    padding-bottom: 8px;
  }

  .gestao-form__remove-col {
    justify-content: flex-start !important;
    margin-top: 4px;
  }
}

@media (max-width: 600px) {
  .gestao-form__relacao {
    border-radius: 16px !important;
  }

  .gestao-form__info-card {
    border-radius: 16px;
  }

  .gestao-form__card-title {
    font-size: 1rem;
    line-height: 1.35;
  }

  .gestao-form__timeline-chip {
    width: 100%;
    justify-content: center;
    text-align: center;
  }
}
</style>
